package com.sddamico.swigmore.ds.forms

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.ds.PressableStyle
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.SwMotion
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeDisplay
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * - [Field]: green fill, cream text — the primary action on a cream card ("Cast Your Ballot").
 * - [Gold]: gold fill, green text — the hero action on the green field ("Submit Ballot").
 * - [Cream]: cream fill, green text — inverse primary on the field.
 * - [Outline]: transparent with a border — secondary ("PREVIEW ANNOUNCEMENT", "WITHDRAW").
 * - [Ghost]: bare text — tertiary ("CLEAR").
 */
enum class ButtonVariant { Field, Gold, Cream, Outline, Ghost }

/**
 * [Display] is the big Graduate face used for the main call to action on each screen; [Caps] is the
 * small tracked Archivo label used for everything else; [CapsSm] is the table-row size.
 */
enum class ButtonStyle { Display, Caps, CapsSm }

/**
 * @param onDark whether the button sits on the green field (affects Outline/Ghost ink colour).
 * @param disabled renders the muted "Fill 2 more slots" state and blocks clicks.
 * @param ai marks an action Claude carries out ("Review ballot", "Rewrite with Claude"): a gold sparkle before
 *   the label, a soft gold glow and an occasional glint across the face.
 * @param sizedFor holds the button at this label's width, so a label that swaps while it works
 *   ("Reviewing…") doesn't move the buttons beside it.
 */
@Composable
fun Button(
  label: String,
  modifier: Modifier = Modifier,
  variant: ButtonVariant = ButtonVariant.Field,
  style: ButtonStyle = ButtonStyle.Display,
  onDark: Boolean = variant == ButtonVariant.Gold || variant == ButtonVariant.Cream,
  disabled: Boolean = false,
  fullWidth: Boolean = false,
  ai: Boolean = false,
  sizedFor: String? = null,
  onClick: (() -> Unit)? = null,
) {
  val handler = onClick
  val ink = if (onDark) SwColor.cream else SwColor.field700
  val surface = when {
    disabled && onDark -> Modifier.css("background" to SwColor.disabledOnField, "color" to SwColor.textMuted)
    disabled -> Modifier.css("background" to SwColor.tintOnCreamStrong, "color" to SwColor.inkFaint)
    else -> when (variant) {
      ButtonVariant.Field -> Modifier.css("background" to SwColor.field700, "color" to SwColor.cream)
      ButtonVariant.Gold -> Modifier.css("background" to SwColor.gold, "color" to SwColor.field700)
      ButtonVariant.Cream -> Modifier.css("background" to SwColor.cream, "color" to SwColor.field700)
      ButtonVariant.Outline -> Modifier.css(
        "background" to "transparent",
        "color" to ink,
        "border" to "1.5px solid ${if (onDark) SwColor.borderStrongOnField else SwColor.field700}",
      )
      ButtonVariant.Ghost -> Modifier.css(
        "background" to "transparent",
        "color" to if (onDark) "rgba(239,229,201,.75)" else SwColor.inkSecondary,
      )
    }
  }
  val type = when (style) {
    ButtonStyle.Display -> Modifier.typeDisplay("17px", "1.2").css("padding" to "13px 16px", "border-radius" to SwEffects.radiusMd)
    ButtonStyle.Caps -> Modifier.typeCaps("11px", ".14em").css("padding" to "10px 14px", "border-radius" to "7px")
    ButtonStyle.CapsSm -> Modifier.typeCaps("9px", ".12em").css("padding" to "6px 10px", "border-radius" to SwEffects.radiusSm)
  }

  DomButton(
    attrs = PressableStyle.toModifier()
      .css("border" to "none", "text-align" to "center", "white-space" to "nowrap", "box-sizing" to "border-box",
        "cursor" to if (disabled) "not-allowed" else "pointer")
      .then(type)
      .then(surface)
      // A disabled Outline keeps its border's width, so it doesn't shrink while it works.
      .then(if (disabled && variant == ButtonVariant.Outline) Modifier.css("border" to "1.5px solid transparent") else Modifier)
      .then(if (fullWidth) Modifier.css("width" to "100%") else Modifier)
      .then(if (ai) Modifier.css("display" to "inline-flex", "align-items" to "center", "justify-content" to "center", "gap" to "6px") else Modifier)
      // The glint is set after [surface], whose `background` shorthand would otherwise clear it.
      .then(if (ai && !disabled) AiButtonStyle.toModifier().css(
        "background-image" to "linear-gradient(110deg, transparent 35%, rgba(217,169,58,.28) 50%, transparent 65%)",
        "background-size" to "250% 100%",
        "background-repeat" to "no-repeat",
        "background-position" to "-50% 0",
      ) else Modifier)
      .then(modifier)
      .toAttrs {
        attr("type", "button")
        if (disabled) attr("disabled", "")
        if (handler != null && !disabled) onClick { handler() }
      }
  ) {
    if (ai) Sparkle(if (style == ButtonStyle.CapsSm) 10 else 12)
    if (sizedFor == null) Text(label)
    else Span(attrs = Modifier.css("display" to "inline-grid").toAttrs()) {
      Span(attrs = Modifier.css("grid-area" to "1 / 1").toAttrs()) { Text(label) }
      Span(attrs = Modifier.css("grid-area" to "1 / 1", "visibility" to "hidden").toAttrs { attr("aria-hidden", "true") }) { Text(sizedFor) }
    }
  }
}

// The box-shadow lives in a style rather than inline so PressableStyle's focus ring still wins on focus.
val AiButtonStyle = CssStyle {
  base {
    Modifier.css(
      "box-shadow" to "0 0 14px rgba(217,169,58,.35)",
      "animation" to "${SwMotion.shimmerKeyframes} 4.5s ease-in-out 1s infinite",
    )
  }
  cssRule(SwMotion.reducedMotion) { Modifier.css("animation" to "none") }
}

/** A four-point star with a small companion, the usual mark for an AI action. */
@Composable
private fun Sparkle(size: Int) {
  Svg(attrs = { viewBox(0, 0, 16, 16); attr("width", "$size"); attr("height", "$size"); attr("aria-hidden", "true") }) {
    Path {
      attr("fill", SwColor.gold)
      attr("d", "M6.5 1C7 4.6 8.4 6 12 6.5 8.4 7 7 8.4 6.5 12 6 8.4 4.6 7 1 6.5 4.6 6 6 4.6 6.5 1Z" +
        "M12.5 10c.2 1.6.9 2.3 2.5 2.5-1.6.2-2.3.9-2.5 2.5-.2-1.6-.9-2.3-2.5-2.5 1.6-.2 2.3-.9 2.5-2.5Z")
    }
  }
}
